// Copy for /services/custom-awards/ (code page since 2026-11-04; gatsby-node
// lists `custom-awards` in hardCodedServices so the Contentful entry stops
// rendering). Same family and terms as /services/custom-trophy/: every price
// and turnaround is read from ./custom-trophy (truth.md rows "Custom-trophy
// pricing" and "Custom-trophy turnaround"). This page targets "custom awards"
// and corporate, employee and service awards; the trophy page keeps
// "custom trophies". No stock catalog, crystal or art-glass awards.
const {
  pricing,
  photos: trophyPhotos,
  photoUrl,
  assetRoot,
} = require('./custom-trophy')

const page = {
  path: '/services/custom-awards/',
  reviewedDate: '2026-11-04',
  reviewedDateLabel: 'November 4, 2026',
  hero: 'logoBubbles',
  title: `Custom Awards for Companies, 3D Printed | From $${pricing.from}`,
  description: `Custom awards made from your logo, product or mascot in New York: corporate, employee and service awards from $${pricing.from} each, no minimum, ships to all 50 states.`,
  answer: `PrintAWorld designs and makes custom awards for companies, agencies and events in our New York studio. We turn your logo, product or mascot into the award itself, send a free concept mockup, then model it in 3D, print it in resin and finish it with paint or metal plating, a base and engraved names. Awards start at $${pricing.from} each, plus a one-time 3D modeling fee ($${pricing.modelingFee} for most projects), with no minimum order. Standard turnaround is 4–6 weeks, rush in 1–2 weeks for many projects, and we ship to all 50 states.`,
}

// Studio photos from the Contentful custom-awards page, plus the trophy
// page's set. Captions say what the award is, not who it was for.
const photos = {
  ...trophyPhotos,
  logoBubbles: {
    file: '4aBDoQgBzDljZO3htc9uP3/872e39c2ead31116e6fc1a4109fa913e/LAL_7270.jpg',
    alt: 'Two blue speech-bubble logo awards on white bases with engraved name plates',
    ratio: 1.5,
  },
  mvpX: {
    file: '7vuINnrl9j6MNylS8HqPCi/37747956ca702e3cfe7fc85873f232d0/schmalz_vacuum_landscape_view.jpg',
    alt: 'A blue X-shaped MVP award built from a company logo, on an engraved wood base',
    ratio: 1.5,
  },
  brain: {
    file: '2kGRUXMD5ZNxm0dX2OFuGO/51a984c81237476d77349c546d6cddf6/LAL_7278.jpg',
    alt: 'A gold-painted brain sculpture on a black base engraved Chief Brain Officer',
    ratio: 1.5,
  },
  astronaut: {
    file: '66cRtyhMDPQtdH6YY6OuqK/eeab6d924d79a036db62742f9004031f/LAL_6391.jpg',
    alt: 'A glossy red astronaut figure award on a round black base',
    ratio: 0.667,
  },
}

const gallery = [
  {
    photo: 'mvpX',
    layout: 'feature',
    caption: 'A company logo turned into an MVP award',
  },
  { photo: 'brain', caption: 'A gold brain for a “Chief Brain Officer”' },
  { photo: 'spark', caption: 'Innovation awards with the program name' },
  {
    photo: 'beamie',
    layout: 'wide',
    caption: 'A full set for an annual awards night',
  },
  { photo: 'audible', caption: 'Painted cube awards in brand colors' },
  { photo: 'goldTowers', caption: 'Gold-plated awards for a premium tier' },
  { photo: 'astronaut', caption: 'A character award on a round base' },
  {
    photo: 'rcCatsSet',
    layout: 'wide',
    caption: 'A team mascot as five matching awards',
  },
]

// "At a glance": the price and turnaround facts AI answers pick vendors on.
const facts = [
  { title: 'Price', body: `From $${pricing.from} per award` },
  {
    title: 'Typical range',
    body: 'Painted resin $250–$750; plated, gold, complex or large pieces $750–$2,000+',
  },
  {
    title: 'Design fee',
    body: `A one-time 3D modeling and design fee, $${pricing.modelingFee} for most projects. The concept mockup is free.`,
  },
  { title: 'Minimum order', body: 'None. One award or a full set.' },
  {
    title: 'Size',
    body: 'Standard awards are 8–12 inches tall; 16 inches and taller start at $1,500.',
  },
  {
    title: 'Turnaround',
    body: '4–6 weeks standard, 6–8 weeks for plated or complex pieces. Rush in 1–2 weeks for many projects.',
  },
  {
    title: 'Personalization',
    body: 'Engraved name plates, logos and dates; we can add names after the event list is final.',
  },
  {
    title: 'Delivery',
    body: 'Made in our New York studios. Pickup by appointment or shipping to all 50 states.',
  },
  {
    title: 'Not sold here',
    body: 'Stock catalog awards, medals, crystal and art-glass awards.',
  },
]

// Corporate award occasions (H2 "Which custom awards do companies order?").
const occasions = [
  {
    title: 'Sales and President’s Club awards',
    body: 'A logo or product sculpture for top closers, with every winner’s name on the plate. Five chrome-effect logo trophies for a sales club is one we’ve made.',
  },
  {
    title: 'Employee and service awards',
    body: 'Years-of-service and milestone awards that look like your company, not a stock plaque. Order a few at a time from the same design as people reach the milestone.',
  },
  {
    title: 'Company values and team awards',
    body: 'One award per value or category, from a single design with a plate for each. A mascot in warm gold, made in rounds every couple of months, is one we’ve done.',
  },
  {
    title: 'Awards nights and industry galas',
    body: 'A stage-ready piece in your program’s colors, in a matched set of winners, or gold, silver and bronze.',
  },
  {
    title: 'Partner and client awards',
    body: 'A partner-of-the-year or store-of-the-year award, sometimes a perpetual piece with blank plates for future years.',
  },
  {
    title: 'Hackathons and internal events',
    body: 'Fun, specific awards: a mascot, an in-joke or the product itself, sized from desk pieces up.',
  },
]

const steps = [
  'Send your logo, product photos or a sketch, how many awards, and your event date.',
  'We send a free concept mockup and a written quote with the price and dates.',
  'We model the award in 3D and you approve it before anything is printed.',
  'We print, sand and paint or plate it, then add the base and engraved plates.',
  'Pick up by appointment in New York, or we ship to you or to each winner.',
]

const comparison = [
  {
    title: 'Catalog award with your logo',
    body: 'A stock acrylic, glass or metal shape with your logo printed or engraved on it. Cheaper and fast; it looks like other companies’ awards.',
  },
  {
    title: 'Custom award from PrintAWorld',
    body: `Your logo, product or mascot sculpted as the award itself, from $${pricing.from} each. Takes 4–6 weeks as standard and costs more, and nobody else has it.`,
  },
  {
    title: 'Somewhere in between',
    body: 'An engraved acrylic award or plaque with your artwork: see our acrylic awards and plaques below.',
  },
]

const fit = {
  good: [
    'Companies that want the award to look like their brand: logo, product or mascot',
    'Sales clubs, service milestones, values awards and awards nights',
    'Agencies producing awards for a client’s event',
    'Small sets, from one award to a few dozen, with names on every plate',
  ],
  other: [
    'Stock medals, crystal or glass awards from a catalog',
    'Hundreds of identical low-cost plaques',
    'An award needed in a few days: our rush is 1–2 weeks',
  ],
}

const sendList = [
  'Your logo (vector or high-resolution) or product photos',
  'What the award is for and how many you need',
  'Names for the plates, if you have them',
  'Size, finish and budget per award, if you know',
  'Your event date and delivery ZIP',
]

const faqs = [
  {
    question: 'How much do custom awards cost?',
    answer: `Custom awards start at $${pricing.from} each. Most painted resin awards are $250–$750; plated, gold, complex or large awards run $750–$2,000+, and pieces 16 inches and taller start at $1,500. A one-time 3D modeling and design fee applies, $${pricing.modelingFee} for most projects. The concept mockup is free.`,
  },
  {
    question: 'Is there a minimum order for custom awards?',
    answer:
      'No. We make one award or a full set. The design fee is paid once per design, so a set from one design costs less per award than several different designs.',
  },
  {
    question: 'How long do custom awards take?',
    answer:
      'Standard turnaround is 4–6 weeks, and 6–8 weeks for plated or complex pieces. Rush in 1–2 weeks is possible for many projects, depending on the design, budget and where it ships. Your written quote confirms the date.',
  },
  {
    question: 'Can you make an award shaped like our logo?',
    answer:
      'Yes, that’s most of what we make. Send a vector logo or a clear image and we model it in 3D, add depth and a base, and paint it in your brand colors. See logo trophies for examples.',
  },
  {
    question: 'Can you make employee and years-of-service awards?',
    answer:
      'Yes. We design one custom award and make it again each time someone reaches the milestone, with the name and years on the plate.',
  },
  {
    question: 'Do you engrave names on the awards?',
    answer:
      'Yes. Names, titles and dates go on engraved plates on the base. If your winners aren’t chosen yet, we can make the awards and add the names when the list is final.',
  },
  {
    question: 'Can you ship awards to each winner?',
    answer:
      'Yes, when it’s in your quote. We ship to all 50 states, to your office or packed separately for each recipient; tell us which when you ask for the quote.',
  },
  {
    question: 'Do you sell crystal or glass awards?',
    answer:
      'No. We don’t sell catalog crystal, art-glass or medal awards. Our awards are sculpted, 3D printed in resin and finished in paint or metal plating.',
  },
  {
    question:
      'What’s the difference between a custom award and a custom trophy?',
    answer:
      'For us, nothing: the same studio, the same process and the same prices. Companies tend to say “awards”; leagues and tournaments say “trophies”. Our custom trophy page has more examples.',
  },
]

module.exports = {
  page,
  photos,
  photoUrl,
  assetRoot,
  pricing,
  gallery,
  facts,
  occasions,
  steps,
  comparison,
  fit,
  sendList,
  faqs,
}
